import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarDays, ExternalLink, Facebook, Instagram, MapPin, Users } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { festivals, formatFestivalDateRange } from '../data/festivals';

const FestivalDetailPage = () => {
  const { id } = useParams();
  const festival = festivals.find((item) => item.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!festival) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-32 pb-24">
          <div className="container-custom">
            <div className="rounded-2xl bg-section-bg p-10 text-center shadow-sm">
              <h1 className="text-3xl font-heading font-bold text-foreground">
                Festivalen blev ikke fundet
              </h1>
              <p className="mt-3 text-body-text">
                Gå tilbage til oversigten og vælg en festival derfra.
              </p>
              <Button asChild className="mt-6">
                <Link to="/festivalkalender">Tilbage til Festival kalender</Link>
              </Button>
            </div>
          </div>
        </main>
        <div className="h-10 bg-white md:h-14" />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main>
        <section className="pt-32 pb-16 md:pt-40 md:pb-20">
          <div className="container-custom">
            <div className="grid gap-12 lg:grid-cols-[1.05fr,0.95fr] lg:items-center">
              <div>
                <div className="mb-4 flex flex-wrap gap-2">
                  {festival.genres?.map((genre) => (
                    <Badge key={genre} variant="secondary" className="rounded-full">
                      {genre}
                    </Badge>
                  ))}
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
                  {festival.name}
                </h1>
                <div className="mt-6 space-y-4 text-body-text">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-accent-blue" />
                    <span>{festival.city}, {festival.region}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CalendarDays className="h-5 w-5 text-accent-blue" />
                    <span>{formatFestivalDateRange(festival.startDate, festival.endDate)}</span>
                  </div>
                  {festival.approxVisitors && (
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-accent-blue" />
                      <span>Ca. {festival.approxVisitors} gæster</span>
                    </div>
                  )}
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  {festival.website && (
                    <Button asChild>
                      <a href={festival.website} target="_blank" rel="noopener noreferrer">
                        Officiel side
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  {festival.instagram && (
                    <Button asChild variant="outline" className="border-primary/20 bg-white text-primary hover:bg-white">
                      <a href={festival.instagram} target="_blank" rel="noopener noreferrer">
                        <Instagram className="h-4 w-4" />
                        Instagram
                      </a>
                    </Button>
                  )}
                  {festival.facebook && (
                    <Button asChild variant="outline" className="border-primary/20 bg-white text-primary hover:bg-white">
                      <a href={festival.facebook} target="_blank" rel="noopener noreferrer">
                        <Facebook className="h-4 w-4" />
                        Facebook
                      </a>
                    </Button>
                  )}
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl shadow-md">
                <img
                  src={festival.image}
                  alt={festival.name}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>

      <div className="h-10 bg-white md:h-14" />
      <Footer />
    </div>
  );
};

export default FestivalDetailPage;
